import React from "react";
import { motion } from "framer-motion";
import type { ChatHistoryResponse } from "@/types/discuss.types";
import { WelcomeMessage } from "./WelcomeMessage";
import { ActiveProcessingMessage } from "./ActiveProcessingMessage";
import { ChatMessageItem } from "./ChatMessageItem";
import MessageLoading from "./MessageLoading";
import ScrollToBottomButton from "./ScrollToBottomButton";

interface ChatMessagesAreaProps {
  historyData: ChatHistoryResponse | undefined;
  isSendingMessage: boolean;
  isCompassProcessing: boolean;
  activeProcessingTask: { prompt?: string } | null | undefined;
  welcomeMessage: string;
  lastMessageRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  doc: { _id?: string; title?: string } | undefined;
  onRelatedQuestionClick: (question: string) => void;
}

export const ChatMessagesArea: React.FC<ChatMessagesAreaProps> = ({
  historyData,
  isSendingMessage,
  isCompassProcessing,
  activeProcessingTask,
  welcomeMessage,
  lastMessageRef,
  scrollContainerRef,
  doc,
  onRelatedQuestionClick,
}) => {
  const hasActiveTask = activeProcessingTask != null;

  return (
    <div className="flex flex-1 min-h-0 relative">
      <div className="flex-1 overflow-y-auto" ref={scrollContainerRef}>
        <div className="flex flex-col gap-3">
          {historyData?.prev_chat?.length === 0 && !isSendingMessage && (
            <WelcomeMessage message={welcomeMessage} />
          )}
          {historyData?.prev_chat?.map((message, index) => (
            <ChatMessageItem
              key={`chat-${index}`}
              message={message}
              index={index}
              isLastItem={index === (historyData?.prev_chat?.length ?? 0) - 1}
              hasActiveTask={hasActiveTask}
              isCompassProcessing={isCompassProcessing}
              isSendingMessage={isSendingMessage}
              lastMessageRef={lastMessageRef}
              doc={doc}
              historyData={historyData}
              onRelatedQuestionClick={onRelatedQuestionClick}
            />
          ))}
          {hasActiveTask && (
            <ActiveProcessingMessage
              activeProcessingTask={activeProcessingTask}
              historyData={historyData}
              lastMessageRef={lastMessageRef}
            />
          )}
          {(isSendingMessage || isCompassProcessing) && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="w-full"
            >
              <MessageLoading text={"AI is thinking"} />
            </motion.div>
          )}
        </div>
      </div>
      <ScrollToBottomButton
        targetRef={lastMessageRef}
        containerRef={scrollContainerRef}
        dependency={historyData?.prev_chat}
      />
    </div>
  );
};
